import { useRef } from "react";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Zap, FileCheck2 } from "lucide-react";
import { IMAGES, PHONE_DISPLAY, PHONE_TEL } from "@/lib/content";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

function RevealLine({ children, delay, reduced }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: reduced ? 0 : "112%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.95, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const reduced = useReducedMotion();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : 110]);

  const rx = useSpring(useMotionValue(0), { stiffness: 120, damping: 16 });
  const ry = useSpring(useMotionValue(0), { stiffness: 120, damping: 16 });

  const onTilt = (e) => {
    if (reduced) return;
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    rx.set(py * -7);
    ry.set(px * 9);
  };
  const resetTilt = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="relative overflow-hidden pt-36 pb-20 sm:pt-44 sm:pb-28"
      style={{ background: "linear-gradient(135deg, #FAF9F6 0%, #F3F1EC 100%)" }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-amber-500/10 blur-3xl"
      />
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 sm:px-10 lg:grid-cols-[1.15fr_0.85fr]">
        <div>
          <motion.div
            data-testid="hero-badge"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-stone-200 bg-white px-4 py-2"
          >
            <span className="pulse-dot h-2 w-2 rounded-full bg-green-700" />
            <span className="font-mono-accent text-[11px] uppercase tracking-[0.2em] text-slate-500">
              Owner-Operated Handyman & Property Care — Deerfield Beach, FL
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
          >
            <RevealLine delay={0.25} reduced={reduced}>
              Precision Repairs &
            </RevealLine>
            <RevealLine delay={0.39} reduced={reduced}>
              Scheduled Care for
            </RevealLine>
            <RevealLine delay={0.53} reduced={reduced}>
              <span className="font-serif-accent font-medium italic text-amber-600">
                South Florida
              </span>{" "}
              Homes.
            </RevealLine>
          </h1>

          <motion.p
            data-testid="hero-subheading"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            Single point of contact. Clear written scopes. Photo-documented
            completion — for homeowners, landlords, and property managers along
            the Atlantic corridor.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-quote-button"
              onClick={() => scrollToId("quote")}
              className="group flex items-center gap-2 rounded-full bg-slate-900 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-amber-600 hover:shadow-xl hover:shadow-amber-600/25"
            >
              Request Immediate Quote
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              data-testid="hero-call-button"
              href={PHONE_TEL}
              className="flex items-center gap-2 rounded-full border border-stone-300 bg-white px-7 py-3.5 text-sm font-semibold text-slate-900 transition-colors duration-300 hover:border-amber-500"
            >
              <Phone className="h-4 w-4 text-amber-600" />
              Call Michael: {PHONE_DISPLAY}
            </a>
          </motion.div>

          <motion.div
            data-testid="hero-trust-badges"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
          >
            {[
              { icon: ShieldCheck, label: "$1M+ Insured" },
              { icon: Zap, label: "Same-Day Dispatch" },
              { icon: FileCheck2, label: "Licensed Partner Referral" },
            ].map(({ icon: Icon, label }) => (
              <span key={label} className="flex items-center gap-2 text-sm font-medium text-slate-500">
                <Icon className="h-4 w-4 text-amber-600" />
                {label}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: imgY }} className="relative" data-testid="hero-visual">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d", perspective: 900 }}
            onMouseMove={onTilt}
            onMouseLeave={resetTilt}
            className="relative"
          >
            <div aria-hidden className="absolute -inset-3 -rotate-2 rounded-[2.2rem] border border-stone-300/70 bg-white/60" />
            <div className="relative overflow-hidden rounded-[2rem] shadow-2xl shadow-slate-900/15">
              <motion.img
                src={IMAGES.heroCraftsman}
                alt="Craftsman performing precision home repair work"
                data-testid="hero-image"
                initial={{ scale: 1.15 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, delay: 0.5, ease: EASE }}
                className="aspect-[4/5] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/35 via-transparent to-transparent" />
            </div>

            <motion.div
              data-testid="hero-status-card"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
              className="absolute -bottom-6 -left-4 rounded-2xl border border-white/50 bg-white/80 px-5 py-4 shadow-xl backdrop-blur-xl sm:-left-10"
              style={{ transform: "translateZ(40px)" }}
            >
              <div className="flex items-center gap-2.5">
                <span className="pulse-dot h-2.5 w-2.5 rounded-full bg-green-700" />
                <span className="text-sm font-semibold text-slate-900">Available today</span>
              </div>
              <p className="font-mono-accent mt-1 text-[11px] uppercase tracking-[0.16em] text-slate-500">
                Broward & Palm Beach dispatch
              </p>
            </motion.div>

            <motion.div
              data-testid="hero-scope-card"
              initial={{ opacity: 0, y: -18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.35, ease: EASE }}
              className="absolute -top-5 -right-3 rounded-2xl border border-white/50 bg-white/80 px-5 py-3.5 shadow-xl backdrop-blur-xl sm:-right-8"
              style={{ transform: "translateZ(60px)" }}
            >
              <p className="font-mono-accent text-[11px] uppercase tracking-[0.16em] text-amber-600">
                Written scope first
              </p>
              <p className="mt-0.5 text-sm font-semibold text-slate-900">Every job. No surprises.</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
